<template>
  <v-dialog
    v-model="isOpen"
    :max-width="hasContents ? 1000 : 760"
    :fullscreen="xs"
    scrollable
    :aria-label="t('releasenotes.dialog.eyebrow')"
    @after-leave="acknowledge"
  >
    <v-card class="release-dialog" :rounded="xs ? 0 : 'xl'">
      <!-- The hero is what the bell entry never had: a moment of its own. Brand colour, full bleed,
           before a single line of feature text — so it reads as news, not as one more alert. -->
      <div class="release-hero">
        <v-btn
          class="release-hero__close"
          icon="mdi-close"
          variant="text"
          size="small"
          density="comfortable"
          :aria-label="t('releasenotes.dialog.close')"
          @click="isOpen = false"
        />
        <div class="release-hero__eyebrow">
          <v-icon size="18" icon="mdi-party-popper" />
          {{ t("releasenotes.dialog.eyebrow") }}
        </div>
        <h2 class="release-hero__title">{{ heading }}</h2>
        <p class="release-hero__text">{{ t("releasenotes.dialog.text") }}</p>
      </div>

      <v-card-text
        ref="body"
        class="release-body"
        :class="{ 'release-body--contents': hasContents }"
        @scroll.passive="follow"
      >
        <!-- With every card's details open the dialog is long. The titles say what is in it, jump there and
             mark where the reader is: a column beside the cards on a wide screen, a row to swipe above
             them on a narrow one. Sticky either way, so it is there wherever the reader has scrolled to. -->
        <nav v-if="hasContents" ref="contents" class="release-contents" :aria-label="t('releasenotes.dialog.contents')">
          <div class="release-contents__label">{{ t("releasenotes.dialog.contents") }}</div>
          <button
            v-for="highlight in highlights"
            :key="highlight.key"
            type="button"
            class="release-contents__item"
            :data-contents="highlight.key"
            :class="{ 'release-contents__item--active': highlight.key === current }"
            :aria-current="highlight.key === current ? 'true' : undefined"
            @click="jumpTo(highlight.key)"
          >
            {{ highlight.title }}
          </button>
        </nav>
        <div class="release-cards">
          <article
            v-for="highlight in highlights"
            :key="highlight.key"
            :data-highlight="highlight.key"
            class="highlight"
          >
            <lew-release-note-media :image="highlight.image" :icon="highlight.icon" />
            <div class="highlight__content">
              <div class="highlight__date">{{ highlight.date }}</div>
              <h3 class="highlight__title">{{ highlight.title }}</h3>
              <lew-release-note-plan class="highlight__plan" :plan="highlight.plan" />
              <p class="highlight__text">{{ highlight.text }}</p>
              <v-btn
                v-if="highlight.route"
                class="highlight__action"
                color="primary"
                variant="tonal"
                size="small"
                append-icon="mdi-arrow-right"
                @click="go(highlight.route)"
              >
                {{ t("releasenotes.open") }}
              </v-btn>
            </div>
            <lew-release-note-details class="highlight__details" :sections="highlight.details" />
          </article>
        </div>
      </v-card-text>

      <v-divider />
      <v-card-actions class="px-4 py-3">
        <v-btn variant="text" @click="go({ name: 'ReleaseNotes' })">
          {{ t("releasenotes.dialog.all") }}
        </v-btn>
        <v-spacer />
        <v-btn color="primary" variant="flat" @click="isOpen = false">
          {{ t("releasenotes.dialog.confirm") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
/**
 * @fileOverview The "Neuigkeiten" dialog: opens once after a release for every user who has not seen it.
 *
 * Replaces the release announcement that used to be pinned to the top of the bell. In the bell it
 * shared a list with "Projekt X läuft ins Minus" and was read — and dismissed — as one more alert.
 * See docs/release-notes.md.
 *
 * Mounted once in App.vue, next to the onboarding tour, for the same reason: LewBaseLayout is remounted
 * on every navigation and could not host anything that has to outlive one.
 */
import { computed, ref, watch } from "vue"
import { useI18n } from "vue-i18n"
import { useRoute, useRouter, type RouteLocationRaw } from "vue-router"
import { useDisplay } from "vuetify"
import { auth } from "@/services/auth"
import { presentRelease } from "@/services/releaseNotes"
import LewReleaseNoteDetails from "@/components/general/LewReleaseNotes/LewReleaseNoteDetails.vue"
import LewReleaseNoteMedia from "@/components/general/LewReleaseNotes/LewReleaseNoteMedia.vue"
import LewReleaseNotePlan from "@/components/general/LewReleaseNotes/LewReleaseNotePlan.vue"
import { useAuthStore } from "@/store/authentication"
import { useReleaseNotesStore } from "@/store/releaseNotes"
import { useOnboardingTour } from "@/composables/useOnboardingTour"
import { useViewHelp } from "@/composables/viewHelp/useViewHelp"

interface ReleaseNote {
  id: string
  releasedOn: string
  highlights: { id: string }[]
  seen: boolean
  announce: boolean
}

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const { xs } = useDisplay()
const authStore = useAuthStore()
const store = useReleaseNotesStore()
const onboarding = useOnboardingTour()
const viewHelp = useViewHelp()

const isOpen = ref(false)
const body = ref<{ $el: HTMLElement } | null>(null)

/**
 * The releases this opening is about, captured when it opens. Not read live from the store: marking
 * them seen flips `announce` off, which would empty the dialog while it is still fading out.
 */
const shown = ref<ReleaseNote[]>([])

/** The user the dialog has already been considered for — once per sign-in, not once per navigation. */
let attemptedFor: string | null = null

const heading = computed(() =>
  authStore.firstName
    ? t("releasenotes.dialog.title", { name: authStore.firstName })
    : t("releasenotes.dialog.title_anonymous")
)

const highlights = computed(() =>
  shown.value.flatMap(release => {
    const { date, highlights } = presentRelease(release)
    return highlights.map(highlight => ({ ...highlight, date }))
  })
)

/** A dialog of more than two cards, every one with its details open, is long enough to need contents. */
const hasContents = computed(() => highlights.value.length > 2)

/** The card the contents mark as the one being read. */
const current = ref<string | null>(null)
const contents = ref<HTMLElement | null>(null)

// In the row of titles on a narrow screen, the marked one moves along with the reader rather than
// scrolling out of the row. The column on a wide screen shows every title and never scrolls sideways.
watch(current, key => {
  const row = contents.value
  if (!row || !key || row.scrollWidth <= row.clientWidth) return
  const item = [...row.querySelectorAll<HTMLElement>("[data-contents]")].find(each => each.dataset.contents === key)
  if (item) row.scrollTo({ left: item.offsetLeft - 20, behavior: "smooth" })
})

/**
 * Whether now is a moment to interrupt somebody. Never before the onboarding tour has been dealt with
 * (a brand-new user has nothing to call "new" — completing the tour marks every release seen on the
 * server), never over another modal, never in the planning pop-out, never on a signed-out page, and
 * not on the changelog, which already shows — and marks seen — everything the dialog would.
 */
const mayOpen = () =>
  auth.isLoggedIn() &&
  authStore.onboardingCompleted === true &&
  route.meta.requiresAuth === true &&
  route.name !== "ReleaseNotes" &&
  route.query.popup !== "true" &&
  !onboarding.isActive.value &&
  !viewHelp.isActive.value

const maybeOpen = async () => {
  // A session that expires while the dialog is up lands on Login underneath it (services/api.js). Close
  // it there: markSeen has no session left to write with, so the releases come back after sign-in.
  if (isOpen.value && !auth.isLoggedIn()) {
    isOpen.value = false
    return
  }
  const userId = authStore.id
  if (!userId || attemptedFor === userId || isOpen.value) return
  // Somebody still due the tour is done being considered for this page load. Finishing it flips the
  // flag locally at once but marks the releases seen on the server a round-trip later, so asking
  // `/releases` right after would announce the whole catalog to a user who has just been shown it.
  if (authStore.onboardingCompleted === false) {
    attemptedFor = userId
    return
  }
  if (!mayOpen()) return
  attemptedFor = userId
  await store.load()
  if (authStore.id !== userId) return
  // The load is a round-trip; somebody may have started the tour or an Erklärung, or opened the
  // changelog, in the meantime. That is "not now", not "not this page load": the watch asks again when
  // they are done.
  if (!mayOpen()) {
    attemptedFor = null
    return
  }
  const announced = store.announced as ReleaseNote[]
  if (!announced.length) return
  shown.value = announced
  current.value = highlights.value[0]?.key ?? null
  isOpen.value = true
}

watch(
  () => [authStore.id, authStore.onboardingCompleted, route.name, onboarding.isActive.value, viewHelp.isActive.value],
  () => maybeOpen(),
  { immediate: true }
)

/**
 * Closing by any path — the button, ✕, Escape, a click outside, or following a highlight — counts as
 * having seen it. The dialog is a one-time moment; the changelog is where it can be read again.
 */
const acknowledge = () => {
  const ids = shown.value.map(release => release.id)
  if (ids.length) store.markSeen(ids)
  shown.value = []
}

/** The cards in the body, in order. */
const cards = () => [...(body.value?.$el.querySelectorAll<HTMLElement>("[data-highlight]") ?? [])]

/**
 * Marks in the contents the card the reader is at: the last one whose top has scrolled past the top of
 * the body. At the very bottom the last cards can never get there, so the last one counts.
 */
const follow = () => {
  const el = body.value?.$el
  if (!el || !hasContents.value) return
  const all = cards()
  const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 2
  const line = el.getBoundingClientRect().top + 96
  const passed = atBottom ? all : all.filter(card => card.getBoundingClientRect().top <= line)
  current.value = (passed[passed.length - 1] ?? all[0])?.dataset.highlight ?? null
}

/** Scrolls the body to the card of [key], from the contents. */
const jumpTo = (key: string) => {
  current.value = key
  const card = cards().find(candidate => candidate.dataset.highlight === key)
  card?.scrollIntoView({ behavior: "smooth", block: "start" })
}

const go = (destination: RouteLocationRaw) => {
  isOpen.value = false
  router.push(destination)
}
</script>

<style scoped>
.release-dialog {
  overflow: hidden;
}

.release-hero {
  position: relative;
  padding: 28px 28px 24px;
  color: rgb(var(--v-theme-on-primary));
  background:
    radial-gradient(circle at 85% 0%, rgba(255, 255, 255, 0.22), transparent 55%),
    linear-gradient(135deg, rgb(var(--v-theme-primary)), rgba(var(--v-theme-primary), 0.78));
}

.release-hero__close {
  position: absolute;
  top: 10px;
  right: 10px;
  color: inherit;
}

.release-hero__eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 10px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.18);
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.release-hero__title {
  margin: 14px 0 6px;
  font-size: 1.5rem;
  font-weight: 600;
  line-height: 1.25;
}

.release-hero__text {
  margin: 0;
  opacity: 0.9;
  line-height: 1.45;
}

.release-body {
  padding: 0 !important;
}

.release-cards {
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
  padding: 20px;
}

/* Narrow: a row of titles to swipe, stuck to the top of the scrolling body. */
.release-contents {
  position: sticky;
  top: 0;
  z-index: 1;
  display: flex;
  gap: 6px;
  padding: 12px 20px;
  overflow-x: auto;
  scrollbar-width: none;
  background: rgb(var(--v-theme-surface));
  border-bottom: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}

.release-contents__label {
  display: none;
}

.release-contents__item {
  flex: none;
  padding: 4px 12px;
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 999px;
  background: transparent;
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
  font: inherit;
  font-size: 0.8125rem;
  line-height: 1.4;
  text-align: left;
  white-space: nowrap;
  transition:
    background-color 0.15s,
    color 0.15s;
}

.release-contents__item:hover {
  background: rgba(var(--v-theme-primary), 0.06);
  color: rgb(var(--v-theme-on-surface));
}

.release-contents__item--active {
  border-color: rgb(var(--v-theme-primary));
  background: rgba(var(--v-theme-primary), 0.1);
  color: rgb(var(--v-theme-primary));
  font-weight: 600;
}

.release-contents__item:focus-visible {
  outline: 2px solid rgb(var(--v-theme-primary));
  outline-offset: 1px;
}

/* A card jumped to lands below the row of titles, not under it. */
.release-body--contents .highlight {
  scroll-margin-top: 64px;
}

/* Wide: a column of titles beside the cards. */
@media (min-width: 960px) {
  .release-body--contents {
    display: grid;
    grid-template-columns: 210px minmax(0, 1fr);
    align-items: start;
  }

  .release-contents {
    flex-direction: column;
    gap: 2px;
    padding: 20px 4px 20px 16px;
    overflow: visible;
    background: none;
    border-bottom: 0;
  }

  .release-contents__label {
    display: block;
    margin: 0 0 6px 10px;
    font-size: 0.7rem;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
  }

  .release-contents__item {
    padding: 6px 10px;
    border: 0;
    border-radius: 8px;
    white-space: normal;
  }

  .release-body--contents .release-cards {
    padding-left: 8px;
  }

  .release-body--contents .highlight {
    scroll-margin-top: 20px;
  }
}

.highlight {
  display: grid;
  grid-template-columns: 240px 1fr;
  gap: 16px;
  padding: 12px;
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 14px;
}

.highlight__content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  min-width: 0;
}

.highlight__date {
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgb(var(--v-theme-primary));
}

.highlight__title {
  margin: 2px 0 4px;
  font-size: 1.05rem;
  font-weight: 600;
  line-height: 1.3;
}

.highlight__plan {
  margin-bottom: 6px;
}

.highlight__text {
  margin: 0 0 12px;
  font-size: 0.875rem;
  line-height: 1.45;
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
}

.highlight__action {
  margin-top: auto;
}

/* Under the still and the text, across the whole card: a list at the text column's width would be twice
   as long. */
.highlight__details {
  grid-column: 1 / -1;
}

@media (max-width: 599px) {
  .highlight {
    grid-template-columns: 1fr;
  }
}
</style>
